import { notification, Spin } from 'antd'
import { t } from 'i18next'
import React, { createContext, useEffect, useRef, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useLocalStorage } from 'react-use'
import { get } from '../Common/api'
import { lbmNotification } from '../Common/notification'
import consumer from '../Common/cable'
import dayjs from 'dayjs'
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint'

const GlobalContext = createContext()
export default GlobalContext

export const GlobalContextProvider = () => {
  const [user, setUser] = useLocalStorage('user', { isAuth: true })
  const [boxes, setBoxes] = useLocalStorage('boxes')
  const [appointments, setAppointments] = useLocalStorage('appointments')
  const [owners, setOwners] = useLocalStorage('owners')
  const [events, setEvents] = useLocalStorage('events', [])
  const [inCharge, setInCharge] = useLocalStorage('inCharge')
  const [filters, setFilters] = useLocalStorage('filters')
  const [tags, setTags] = useLocalStorage('tags')
  const [openChats, setOpenChats] = useLocalStorage('chats')
  const [box, setBox] = useState()
  const [appointment, setAppointment] = useState()
  const [event, setEvent] = useState()
  const [openChat, setOpenChat] = useState()
  const [loading, setLoading] = useState(false)
  const notificationOnRef = useRef(false)
  const [notificationApi, notificationContextHolder] = notification.useNotification()
  const screens = useBreakpoint()

  // Appointments state
  const [appointmentsDate, setAppointmentsDate] = useState(dayjs())

  const navigate = useNavigate()

  useEffect(() => {
    const boxSubscription = consumer.subscriptions.create({ channel: 'BoxesChannel' }, {
      connected: () => { console.log('box connected') },
      disconnected: () => { console.log('box disconnected') },
      received: (res) => { setBox(res.box) }
    })
    const appointmentSubscription = consumer.subscriptions.create({ channel: 'AppointmentsChannel' }, {
      connected: () => { console.log('appointment connected') },
      disconnected: () => { console.log('appointment disconnected') },
      received: (res) => { setAppointment(res.appointment) }
    })
    const eventSubscription = consumer.subscriptions.create({ channel: 'EventsChannel' }, {
      connected: () => { console.log('event connected') },
      disconnected: () => { console.log('event disconnected') },
      received: (res) => { setEvent({ ...res, content: res.content_trix_html }) }
    })
    const chatSubscription = consumer.subscriptions.create({ channel: 'OpenChatsChannel' }, {
      connected: () => { console.log('chat connected') },
      disconnected: () => { console.log('chat disconnected') },
      received: (res) => { setOpenChat(res.chat) }
    })
    let notifSubscription = false
    if (!notificationOnRef.current) {
      notificationOnRef.current = true
      notifSubscription = consumer.subscriptions.create({ channel: 'NotifyChannel' }, {
        connected: () => { console.log('notif connected') },
        disconnected: () => { console.log('notif disconnected') },
        received: (res) => { lbmNotification(navigate, res, notificationApi) }
      })
    }
    getBoxes()
    getInCharge()
    getOwners()
    getFilters()
    getTags()
    getOpenChats()
    return () => {
      boxSubscription.unsubscribe()
      appointmentSubscription.unsubscribe()
      eventSubscription.unsubscribe()
      chatSubscription.unsubscribe()
      if (notifSubscription) {
        notifSubscription.unsubscribe()
      }
    }
  }, [consumer])

  useEffect(() => {
    if (box) {
      updateBoxes([box])
    }
  }, [box])

  useEffect(() => {
    if (appointment) {
      updateAppointments([appointment])
    }
  }, [appointment])

  useEffect(() => {
    if (event) {
      updateEvents([event])
    }
  }, [event])

  useEffect(() => {
    if (openChat) {
      updateOpenChats([openChat])
    }
  }, [openChat])

  const errorMessages = (errors, traductionPath) => {
    return Object.keys(errors).map(key => (
      <React.Fragment key={key}>{t(traductionPath + '.' + key) + ': ' + errors[key]}</React.Fragment>
    ))
  }

  const getAppointments = async () => {
    setLoading(true)
    await get({
      url: 'admin/appointments',
      params: { scope: 'today' },
      onSuccess: (res) => {
        setAppointments(res.map((appointment) => ({ ...appointment, assigned_short_name: !appointment.assigned_short_name ? '' : appointment.assigned_short_name })))
        setLoading(false)
      }
    })
  }

  const updateAppointments = (newAppointments) => {
    let savedAppointments = appointments || []
    newAppointments = newAppointments.map((appointment) => ({ ...appointment, assigned_short_name: !appointment.assigned_short_name ? '' : appointment.assigned_short_name }))
    newAppointments.forEach((appointment) => {
      const index = savedAppointments.findIndex((b) => b.id === appointment.id)
      if (index !== -1) {
        savedAppointments[index] = { ...savedAppointments[index], ...appointment }
      } else {
        savedAppointments = [...savedAppointments, appointment]
      }
    })
    setAppointments([...savedAppointments].sort((a, b) => dayjs(a.begin_date).diff(dayjs(b.begin_date))))
  }

  const getUnresolvedEvents = async () => {
    await get({
      url: 'admin/events',
      params: { resolved: false },
      notification: { api: notificationApi, state: 'get' },
      onSuccess: (res) => {
        updateEvents(res)
      }
    })
  }

  const getEvents = async (eventFilter) => {
    await get({
      url: 'admin/events',
      params: { ...eventFilter },
      onSuccess: (res) => {
        updateEvents(res)
      }
    })
  }

  const updateEvents = (newEvents) => {
    let savedEvents = events || []
    newEvents.forEach((event) => {
      const index = savedEvents.findIndex((b) => b.id === event.id)
      if (index !== -1) {
        savedEvents[index] = { ...savedEvents[index], ...event }
      } else {
        savedEvents = [...savedEvents, event]
      }
    })
    setEvents([...savedEvents])
  }

  const getBoxes = async () => {
    await get({
      url: 'admin/boxes',
      params: {},
      notification: { api: notificationApi, state: 'get' },
      onSuccess: (res) => {
        updateBoxes(res)
      }
    })
  }

  const updateBoxes = (newBoxes) => {
    let savedBoxes = boxes || []
    newBoxes.forEach((box) => {
      const index = savedBoxes.findIndex((b) => b.id === box.id)
      if (index !== -1) {
        savedBoxes[index] = { ...savedBoxes[index], ...box }
      } else if (box.name) {
        savedBoxes = [...savedBoxes, box]
      }
    })
    setBoxes([...savedBoxes])
  }

  const getInCharge = async () => {
    await get({
      url: 'admin/events/in_charge_options',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setInCharge(res)
      }
    })
  }

  const getOwners = async () => {
    await get({
      url: 'admin/owners',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setOwners(res)
      }
    })
  }

  const getFilters = async () => {
    await get({
      url: 'admin/event_filters/user',
      notification: { api: notificationApi, state: 'get' },
      params: { user_id: user.id },
      onSuccess: (res) => {
        setFilters(res)
      }
    })
  }

  const updateFilters = (filter) => {
    let savedFilters = filters || []
    const index = filters.findIndex((b) => b.id === filter.id)
    if (index !== -1) {
      savedFilters[index] = { ...savedFilters[index], ...filter }
    } else {
      savedFilters = [...savedFilters, filter]
    }
    setFilters([...savedFilters])
  }

  const getTags = async () => {
    await get({
      url: 'admin/tags/all',
      notification: { api: notificationApi, state: 'get' },
      params: {},
      onSuccess: (res) => {
        setTags(res)
      }
    })
  }

  const getOpenChats = async () => {
    await get({
      url: 'admin/chats',
      notification: { api: notificationApi, state: 'get' },
      params: { filter: 'open' },
      onSuccess: (res) => {
        setOpenChats(res)
      }
    })
  }

  const updateOpenChats = (newOpenChats) => {
    let savedOpenChats = openChats || []
    newOpenChats.forEach((chat) => {
      const index = savedOpenChats.findIndex((e) => e.id === chat.id)
      if (index !== -1) {
        savedOpenChats[index] = { ...savedOpenChats[index], ...chat }
      } else {
        savedOpenChats = [...savedOpenChats, chat]
      }
    })
    setOpenChats([...savedOpenChats])
  }

  const values = {
    // localStorage
    user,
    setUser,
    appointments,
    boxes,
    owners,
    events,
    setEvents,
    inCharge,
    filters,
    tags,
    openChats,

    // getter
    getAppointments,
    getBoxes,
    getOwners,
    getEvents,
    getUnresolvedEvents,
    getFilters,
    getTags,

    // updater
    updateAppointments,
    updateBoxes,
    updateEvents,
    updateFilters,

    // methods
    errorMessages,
    notificationApi,
    appointmentsDate,
    setAppointmentsDate,
    openChat,
    screens
  }

  const defaultLoaded = boxes && inCharge && owners && filters && tags && openChats

  return <GlobalContext.Provider value={values}>
    <Spin spinning={!defaultLoaded || loading}>
      {notificationContextHolder}
      { defaultLoaded && <Outlet /> }
    </Spin>
  </GlobalContext.Provider>
}
